// All of the app's state and business logic (loading/saving, plan
// assignment, grocery-list derivation, recipe CRUD) in one hook — this is
// the direct port of what used to be local state + effects inside the
// top-level <MealPlanner> component in WEEKLY_2.HTM. Pulling it out into a
// hook (consumed via MealPlannerProvider, see App.jsx) is what lets the
// former "tabs" become real routed pages that all share the same data.
//
// The load effect and the three debounced save effects below are the only
// places that talk to db.js, which reads/writes the relational Supabase
// tables (see supabase/migrations/0002_relational_schema.sql) — this file's
// state-update/business logic didn't need to change for that, since db.js's
// load*/save* functions hydrate/dehydrate the exact same shapes this hook
// already worked with.
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  loadRecipes,
  loadSharedWithMeRecipeIds,
  loadMySharedRecipeIds,
  createRecipe as dbCreateRecipe,
  updateRecipe as dbUpdateRecipe,
  leaveOrDeleteRecipe as dbLeaveOrDeleteRecipe,
  importRecipes as dbImportRecipes,
  importRecipeFromUrl as dbImportRecipeFromUrl,
  registerRecipeUrlImport as dbRegisterRecipeUrlImport,
  claimRecipeUrlImport as dbClaimRecipeUrlImport,
  toggleRecipeFavorite as dbToggleFavorite,
  loadMyFavoriteRecipeIds,
  setMyRecipeFavorite as dbSetMyRecipeFavorite,
  loadWeekPlans,
  saveWeekMeta,
  saveWeekPlanMeal,
  deleteWeekPlanMealRow,
  setWeekPlanMealCooked,
  savePlacementChanges,
  setRecipePublic as dbSetRecipePublic,
  addWeekPlanItem,
  removeWeekPlanItem,
  clearWeekPlanDay,
  clearWeekPlanWeek,
  replaceWeekPlanWeek,
  loadPlanDefaults,
  savePlanDefaults,
  loadInventoryItems,
  addInventoryItem as dbAddInventoryItem,
  updateInventoryItem as dbUpdateInventoryItem,
  deleteInventoryItem as dbDeleteInventoryItem,
} from "../lib/db";
import { DAYS, MEAL_SLOTS, DINING_OUT } from "../lib/constants";
import { uid, parseQuantity, formatQuantity, round, currentWeekStart } from "../lib/utils";
import { buildGroceryList } from "../lib/groceryUtils";
import { defaultPlanDefaults, buildDefaultWeek, normalizeDishes, weekCookMinutes } from "../lib/planUtils";
import { SEED_RECIPES, SEED_TITLES } from "../lib/seedRecipes";
import { groceryPlacements, followSourceChange, detachWhere, linkTo, shiftLinks, mealKey, isCooked } from "../lib/planScaling";
import { moveMeal as moveMealIn, placementChanges } from "../lib/planMoves";

export function useMealPlannerData(householdId, userId) {
  const [loaded, setLoaded] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [sharedWithMeIds, setSharedWithMeIds] = useState(new Map());
  const [mySharedRecipeIds, setMySharedRecipeIds] = useState(new Set());
  const [myFavoriteRecipeIds, setMyFavoriteRecipeIds] = useState(new Set());
  const [plansByWeek, setPlansByWeek] = useState({});
  const [inventoryByWeek, setInventoryByWeek] = useState({});
  const [planDefaults, setPlanDefaults] = useState(defaultPlanDefaults());
  const [weekStart, setWeekStart] = useState(currentWeekStart());
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!householdId || !userId) return;
    async function load() {
      // These six reads are independent of each other (different tables,
      // no data one needs from another), so they're fired off together and
      // awaited via Promise.all rather than one-at-a-time -- awaiting each
      // in turn blocked the next request from even starting, turning what
      // should be one round trip's worth of latency into the sum of all six.
      // Each task keeps its own try/catch with the same fallback as before,
      // so one table being unreachable still degrades independently instead
      // of failing the whole load.
      const recipesTask = (async () => {
        try {
          const r = await loadRecipes();
          const loadedRecipes = r.length > 0 ? r : SEED_RECIPES;
          setRecipes(
            loadedRecipes.map((rec) => ({
              ...rec,
              seasons: rec.seasons || [],
              source: rec.source != null ? rec.source : SEED_TITLES.includes(rec.title) ? "Generic Seeding" : "",
              favorite: !!rec.favorite,
              mealTypes: rec.mealTypes || (rec.mealType ? [rec.mealType] : []),
              recipeType: rec.recipeType || rec.dishType || "entree",
              dietTags: rec.dietTags || [rec.vegetarian && "vegetarian", rec.vegan && "vegan", rec.glutenFree && "glutenFree"].filter(Boolean),
              cookingMethod: rec.cookingMethod || [],
              yield: rec.yield || "",
              caloriesPerServing: rec.caloriesPerServing || 0,
              proteinPerServing: rec.proteinPerServing || 0,
              carbsPerServing: rec.carbsPerServing || 0,
              fatPerServing: rec.fatPerServing || 0,
              // Seed recipes (a first sign-in with no recipes yet) carry none
              // of these -- default to "mine, private, unlocked" so they
              // behave exactly as they did before sharing existed.
              ownerUserId: rec.ownerUserId ?? userId,
              isPublic: !!rec.isPublic,
              editableByOthers: !!rec.editableByOthers,
              ingredients: rec.ingredients.map((ing) => ({
                ...ing,
                component: ing.component || "",
                type: ing.type === "note" ? "note" : "ingredient",
                category: ing.category === "grains" ? "bulk" : ing.category,
              })),
              instructions: (rec.instructions || []).map((s) =>
                typeof s === "string"
                  ? { id: uid(), component: "", type: "step", text: s }
                  : { id: s.id || uid(), component: s.component || "", type: s.type === "note" ? "note" : "step", text: s.text || "" }
              ),
            }))
          );
        } catch {
          setRecipes(SEED_RECIPES);
        }
      })();

      const sharedWithMeTask = (async () => {
        try {
          setSharedWithMeIds(await loadSharedWithMeRecipeIds(userId));
        } catch {
          setSharedWithMeIds(new Map());
        }
      })();

      const mySharedTask = (async () => {
        try {
          setMySharedRecipeIds(await loadMySharedRecipeIds(userId));
        } catch {
          setMySharedRecipeIds(new Set());
        }
      })();

      const favoritesTask = (async () => {
        try {
          setMyFavoriteRecipeIds(await loadMyFavoriteRecipeIds(userId));
        } catch {
          setMyFavoriteRecipeIds(new Set());
        }
      })();

      const weekPlansTask = (async () => {
        try {
          setPlansByWeek(await loadWeekPlans(householdId));
        } catch {
          setPlansByWeek({});
        }
      })();

      const planDefaultsTask = (async () => {
        let resolvedDefaults = defaultPlanDefaults();
        try {
          const loadedDefaults = await loadPlanDefaults(householdId, userId);
          resolvedDefaults = { ...defaultPlanDefaults(), ...loadedDefaults };
        } catch {
          // fall through with the plain defaults already assigned above
        }
        setPlanDefaults(resolvedDefaults);
        setWeekStart(currentWeekStart(resolvedDefaults.weekStartDay));
      })();

      const inventoryTask = (async () => {
        try {
          setInventoryByWeek(await loadInventoryItems(householdId));
        } catch {
          setInventoryByWeek({});
        }
      })();

      await Promise.all([recipesTask, sharedWithMeTask, mySharedTask, favoritesTask, weekPlansTask, planDefaultsTask, inventoryTask]);
      setLoaded(true);
    }
    load();
  }, [householdId, userId]);

  // Recipes are no longer synced via a debounced whole-array effect (see
  // db.js's "recipes" section header comment for why -- the array can hold
  // rows owned by other users now) -- saveRecipe/importRecipes/
  // toggleFavorite/scaleRecipe/deleteRecipe below each call db.js directly,
  // scoped to the one recipe they touch.

  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      savePlanDefaults(householdId, planDefaults, userId)
        .then(() => setSaveError(false))
        .catch((e) => {
          console.error("[savePlanDefaults] failed:", e);
          setSaveError(true);
        });
    }, 600);
    return () => clearTimeout(t);
  }, [planDefaults, loaded, householdId, userId]);

  const weekData = useMemo(() => plansByWeek[weekStart] || buildDefaultWeek(planDefaults), [plansByWeek, weekStart, planDefaults]);
  const inventoryItems = useMemo(() => inventoryByWeek[weekStart] || [], [inventoryByWeek, weekStart]);

  // Plain per-row CRUD (see db.js's inventory section) -- household_id comes
  // from context, not the caller, the same boundary as everywhere else.
  async function addInventoryItem(item) {
    try {
      const created = await dbAddInventoryItem(householdId, weekStart, item);
      setInventoryByWeek((prev) => ({ ...prev, [weekStart]: [...(prev[weekStart] || []), created] }));
      setSaveError(false);
      return created;
    } catch (e) {
      console.error("[addInventoryItem] failed:", e);
      setSaveError(true);
      throw e;
    }
  }

  async function updateInventoryItem(id, item) {
    try {
      await dbUpdateInventoryItem(id, item);
      setInventoryByWeek((prev) => ({ ...prev, [weekStart]: (prev[weekStart] || []).map((i) => (i.id === id ? { ...i, ...item } : i)) }));
      setSaveError(false);
    } catch (e) {
      console.error("[updateInventoryItem] failed:", e);
      setSaveError(true);
      throw e;
    }
  }

  async function deleteInventoryItem(id) {
    try {
      await dbDeleteInventoryItem(id);
      setInventoryByWeek((prev) => ({ ...prev, [weekStart]: (prev[weekStart] || []).filter((i) => i.id !== id) }));
      setSaveError(false);
    } catch (e) {
      console.error("[deleteInventoryItem] failed:", e);
      setSaveError(true);
      throw e;
    }
  }

  // Piece B of the collaborative-editing follow-up: this used to be one
  // effect that re-saved EVERY week's meals/items on ANY plansByWeek
  // change, no matter what actually changed -- see saveWeekPlans's removal
  // comment in db.js for why that's unsafe once a second household can
  // also write to the same week. Meals/items are now saved directly by
  // each mutator below (setDish, addSnack, etc. in PlanPage.jsx, and
  // assignRecipeToPlan/removeRecipeFromPlan here) at the moment they
  // happen, targeted to the exact row -- this effect only covers the
  // current week's title/servings/dayBudgets, which are genuinely
  // "typing" fields worth debouncing, and only for the ONE week actually
  // being viewed.
  const weekMeta = useMemo(() => ({ title: weekData.title, servings: weekData.servings, dayBudgets: weekData.dayBudgets }), [weekData.title, weekData.servings, weekData.dayBudgets]);

  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      saveWeekMeta(householdId, weekStart, weekMeta)
        .then(() => setSaveError(false))
        .catch((e) => {
          console.error("[saveWeekMeta] failed:", e);
          setSaveError(true);
        });
    }, 600);
    return () => clearTimeout(t);
  }, [weekMeta, weekStart, loaded, householdId]);

  const updateCurrentWeek = useCallback(
    (updater) => {
      setPlansByWeek((prev) => {
        const current = prev[weekStart] || buildDefaultWeek(planDefaults);
        return { ...prev, [weekStart]: updater(current) };
      });
    },
    [weekStart, planDefaults]
  );

  // Moved here from PlanPage.jsx as part of piece B of the
  // collaborative-editing follow-up -- these need direct access to
  // householdId/weekStart/weekMeta/setSaveError to persist per-cell,
  // targeted, the same way assignRecipeToPlan/removeRecipeFromPlan already
  // do, rather than relying on a debounced effect to infer everything from
  // the end state.
  // Mirrors 0053's triggers in local state, so the screen agrees with the
  // database without a reload. `cells` are placements in the current week
  // that just changed, as { day, slot, dishType, recipeId, isLeftover }: a
  // cell that is no longer leftovers drops its own link, and leftovers
  // anywhere that draw on a cell follow its new recipe, or lose the link if
  // it was cleared or became dining out or leftovers.
  //
  // It also drops a cell's cooked mark as 0058's trigger does: when the
  // cell stops being a cooked dish or gets another recipe. That is judged
  // here, while plansByWeek still holds the cell as it was.
  function syncLinks(cells) {
    const before = plansByWeek[weekStart];
    const uncook = cells
      .filter((c) => {
        const was = normalizeDishes(before?.days?.[c.day]?.[c.slot])[c.dishType] || "";
        return !c.recipeId || c.recipeId === DINING_OUT || c.isLeftover || was !== c.recipeId;
      })
      .map((c) => mealKey(c.day, c.slot, c.dishType));
    setPlansByWeek((prev) => {
      let next = prev;
      const wd = next[weekStart];
      if (wd?.cooked && uncook.some((k) => wd.cooked[k])) {
        const cooked = { ...wd.cooked };
        uncook.forEach((k) => delete cooked[k]);
        next = { ...next, [weekStart]: { ...wd, cooked } };
      }
      const current = next[weekStart];
      if (current?.leftoverSources) {
        const leftoverSources = { ...current.leftoverSources };
        cells.forEach((c) => {
          if (!c.isLeftover) delete leftoverSources[mealKey(c.day, c.slot, c.dishType)];
        });
        next = { ...next, [weekStart]: { ...current, leftoverSources } };
      }
      return cells.reduce(
        (acc, c) => followSourceChange(acc, { weekStart, day: c.day, mealSlot: c.slot, dishType: c.dishType }, { recipeId: c.recipeId, isLeftover: c.isLeftover }),
        next
      );
    });
  }

  function setDish(day, slot, dishType, recipeId) {
    updateCurrentWeek((wd) => {
      const days = { ...wd.days };
      const daySlots = { ...(days[day] || {}) };
      const mealDishes = { ...normalizeDishes(daySlots[slot]) };
      mealDishes[dishType] = recipeId || "";
      daySlots[slot] = mealDishes;
      days[day] = daySlots;
      const leftovers = { ...(wd.leftovers || {}) };
      delete leftovers[`${day}|${slot}|${dishType}`];
      return { ...wd, days, leftovers };
    });
    syncLinks([{ day, slot, dishType, recipeId: recipeId || "", isLeftover: false }]);
    saveWeekPlanMeal(householdId, weekStart, day, slot, dishType, recipeId, false, weekMeta)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[setDish] failed:", e);
        setSaveError(true);
      });
  }

  function removeDishRow(day, slot, dishType) {
    updateCurrentWeek((wd) => {
      const days = { ...wd.days };
      const daySlots = { ...(days[day] || {}) };
      const mealDishes = { ...normalizeDishes(daySlots[slot]) };
      delete mealDishes[dishType];
      daySlots[slot] = mealDishes;
      days[day] = daySlots;
      const leftovers = { ...(wd.leftovers || {}) };
      delete leftovers[`${day}|${slot}|${dishType}`];
      return { ...wd, days, leftovers };
    });
    syncLinks([{ day, slot, dishType, recipeId: "", isLeftover: false }]);
    deleteWeekPlanMealRow(householdId, weekStart, day, slot, dishType)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[removeDishRow] failed:", e);
        setSaveError(true);
      });
  }

  // Marks a planned dish cooked or not (slice 7c, 0058): from the day view's
  // tick and "Done cooking", for any loaded week. Only a cooked dish (a
  // recipe, not leftovers or dining out) can be marked.
  function setCooked(ofWeek, day, slot, dishType, cooked) {
    const week = plansByWeek[ofWeek];
    const key = mealKey(day, slot, dishType);
    const recipeId = normalizeDishes(week?.days?.[day]?.[slot])[dishType];
    if (!week || (cooked && (!recipeId || recipeId === DINING_OUT || week.leftovers?.[key]))) return;
    const at = cooked ? new Date().toISOString() : null;
    setPlansByWeek((prev) => {
      const wd = prev[ofWeek];
      if (!wd) return prev;
      const marks = { ...(wd.cooked || {}) };
      if (at) marks[key] = at;
      else delete marks[key];
      return { ...prev, [ofWeek]: { ...wd, cooked: marks } };
    });
    setWeekPlanMealCooked(householdId, ofWeek, day, slot, dishType, at)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[setCooked] failed:", e);
        setSaveError(true);
      });
  }

  // Re-reads the whole plan from the database: the grocery list calls it
  // when another household member (or a partner household) changes the plan
  // while the list is open (slice 6a). Keeps what it had if the read fails.
  const reloadWeekPlans = useCallback(async () => {
    if (!householdId) return;
    try {
      setPlansByWeek(await loadWeekPlans(householdId));
    } catch (e) {
      console.error("[reloadWeekPlans] failed:", e);
    }
  }, [householdId]);

  // Moves a whole meal to another day or meal of this week, swapping with
  // whatever is planned there; leftovers links move with it (planMoves.ts).
  // `from` and `to` are { day, slot }.
  function moveMeal(from, to) {
    const now = new Date();
    const next = moveMealIn(plansByWeek, weekStart, from, to, now);
    if (next === plansByWeek) return;
    const changes = placementChanges(plansByWeek, next);
    setPlansByWeek((prev) => moveMealIn(prev, weekStart, from, to, now));
    savePlacementChanges(householdId, weekStart, changes, weekMeta)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[moveMeal] failed:", e);
        setSaveError(true);
      });
  }

  // Fills a slot with leftovers (slice 5b's day view; the picker in 5c):
  // either of a planned meal -- `{ source }`, a MealRef, which is linked and
  // scales that meal up -- or of any recipe with no source -- `{ recipeId }`,
  // "Something older", never scaled.
  function placeLeftovers(day, slot, dishType, { source = null, recipeId: olderRecipeId = null }) {
    const recipeId = source ? normalizeDishes(plansByWeek[source.weekStart]?.days?.[source.day]?.[source.mealSlot])[source.dishType] : olderRecipeId;
    if (!recipeId || recipeId === DINING_OUT) return;
    const key = mealKey(day, slot, dishType);
    const ref = source && { weekStart: source.weekStart, day: source.day, mealSlot: source.mealSlot, dishType: source.dishType };
    const link = ref ? linkTo(ref, new Date(), isCooked(plansByWeek, ref)) : null;
    updateCurrentWeek((wd) => {
      const days = { ...wd.days };
      const daySlots = { ...(days[day] || {}) };
      daySlots[slot] = { ...normalizeDishes(daySlots[slot]), [dishType]: recipeId };
      days[day] = daySlots;
      const leftovers = { ...(wd.leftovers || {}), [key]: true };
      const leftoverSources = { ...(wd.leftoverSources || {}) };
      if (link) leftoverSources[key] = link;
      else delete leftoverSources[key];
      return { ...wd, days, leftovers, leftoverSources };
    });
    syncLinks([{ day, slot, dishType, recipeId, isLeftover: true }]);
    saveWeekPlanMeal(householdId, weekStart, day, slot, dishType, recipeId, true, weekMeta, link)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[placeLeftovers] failed:", e);
        setSaveError(true);
      });
  }

  // dayBudgets lives on the week_plans row's own fields, so it's covered by
  // the debounced weekMeta effect above -- no separate persist call needed
  // here, just the local update.
  function setDayBudget(day, minutes) {
    updateCurrentWeek((wd) => {
      const dayBudgets = { ...(wd.dayBudgets || {}) };
      if (minutes == null) delete dayBudgets[day];
      else dayBudgets[day] = minutes;
      return { ...wd, dayBudgets };
    });
  }

  // clear_week_plan_day (via clearWeekPlanDay) is owner-only, enforced
  // server-side -- see its comment in
  // 0019_week_plan_visibility_lockdown.sql. Only ever wired to a button in
  // PlanPage.jsx, which only ever operates on the signed-in household's
  // own current week.
  function clearDay(day) {
    updateCurrentWeek((wd) => {
      const leftovers = { ...(wd.leftovers || {}) };
      const leftoverSources = { ...(wd.leftoverSources || {}) };
      Object.keys(leftovers).forEach((k) => {
        if (k.startsWith(`${day}|`)) delete leftovers[k];
      });
      Object.keys(leftoverSources).forEach((k) => {
        if (k.startsWith(`${day}|`)) delete leftoverSources[k];
      });
      const cooked = Object.fromEntries(Object.entries(wd.cooked || {}).filter(([k]) => !k.startsWith(`${day}|`)));
      return { ...wd, days: { ...wd.days, [day]: {} }, leftovers, leftoverSources, cooked };
    });
    setPlansByWeek((prev) => detachWhere(prev, (src) => src.weekStart === weekStart && src.day === day));
    clearWeekPlanDay(householdId, weekStart, day)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[clearDay] failed:", e);
        setSaveError(true);
      });
  }

  // Generic add/remove for the three week-scoped item lists
  // (snacks/preserves/medicinal) -- awaits the insert/delete rather than
  // updating local state optimistically first, since an item row's id is
  // server-generated (nothing to optimistically render it under until the
  // insert returns) -- same reasoning as assignRecipeToPlan's item branch.
  const WEEK_ITEM_FIELDS = { snack: "snacks", preserves: "preserves", medicinal: "medicinal" };

  async function addWeekItem(listType, recipeId) {
    if (!recipeId) return;
    const field = WEEK_ITEM_FIELDS[listType];
    try {
      const itemId = await addWeekPlanItem(householdId, weekStart, listType, recipeId, weekMeta);
      updateCurrentWeek((wd) => ({ ...wd, [field]: [...(wd[field] || []), { id: itemId, recipeId }] }));
      setSaveError(false);
    } catch (e) {
      console.error("[addWeekItem] failed:", e);
      setSaveError(true);
    }
  }

  async function removeWeekItem(listType, index) {
    const field = WEEK_ITEM_FIELDS[listType];
    const entry = weekData[field]?.[index];
    if (!entry) return;
    updateCurrentWeek((wd) => ({ ...wd, [field]: (wd[field] || []).filter((e) => e.id !== entry.id) }));
    try {
      await removeWeekPlanItem(entry.id);
      setSaveError(false);
    } catch (e) {
      console.error("[removeWeekItem] failed:", e);
      setSaveError(true);
    }
  }

  // Wholesale-replaces the current week from a weekData-shaped snapshot --
  // a past week (applyWeekAsTemplate) or a meal_plan_templates row
  // (applyPublicTemplateAsCurrentWeek, see db.js's templateSnapshotToWeekData).
  // Both sources are read-only as far as this function is concerned; the
  // destination is always this household's OWN current week, so the old
  // delete-then-reinsert shape is still fine here (replaceWeekPlanWeek),
  // just scoped to the one week instead of every week in memory. Reconciles
  // the returned item rows' real ids into local state -- copying a
  // template always creates brand new week_plan_items rows, never reuses
  // the source's ids.
  async function applyWeekDataAsCurrentWeek(cloned, errorLabel) {
    // The week's old rows are deleted, which clears links into it from other
    // weeks; the copy brings its own links (see applyWeekAsTemplate).
    // A copied week starts with nothing marked cooked (its rows are new).
    cloned = { ...cloned, cooked: {} };
    setPlansByWeek((prev) => ({ ...detachWhere(prev, (src) => src.weekStart === weekStart), [weekStart]: cloned }));
    try {
      const newItemRows = await replaceWeekPlanWeek(householdId, weekStart, cloned);
      const byType = { snack: [], preserves: [], medicinal: [] };
      newItemRows.forEach((row) => {
        if (byType[row.list_type]) byType[row.list_type].push({ id: row.id, recipeId: row.recipe_id });
      });
      setPlansByWeek((prev) => {
        const wd = prev[weekStart];
        if (!wd) return prev;
        return { ...prev, [weekStart]: { ...wd, snacks: byType.snack, preserves: byType.preserves, medicinal: byType.medicinal } };
      });
      setSaveError(false);
    } catch (e) {
      console.error(`[${errorLabel}] failed:`, e);
      setSaveError(true);
    }
  }

  function applyWeekAsTemplate(sourceWeekKey) {
    const source = plansByWeek[sourceWeekKey];
    if (!source) return;
    const cloned = JSON.parse(JSON.stringify(source));
    cloned.leftoverSources = shiftLinks(source.leftoverSources || {}, sourceWeekKey, weekStart, cloned, plansByWeek, new Date());
    return applyWeekDataAsCurrentWeek(cloned, "applyWeekAsTemplate");
  }

  // Piece D (public plan templates): applies a meal_plan_templates row --
  // either the household's own, or a public one someone else published --
  // to the current week. templateToWeekData is db.js's
  // templateSnapshotToWeekData, passed in rather than imported here so
  // this file doesn't need a new import purely for a shape conversion the
  // caller (PublicPlansPage.jsx) already has to do anyway to preview the
  // template before applying it.
  function applyPublicTemplateAsCurrentWeek(templateWeekData) {
    return applyWeekDataAsCurrentWeek(JSON.parse(JSON.stringify(templateWeekData)), "applyPublicTemplateAsCurrentWeek");
  }

  // clear_week_plan_week (called via clearWeekPlanWeek) is owner-only,
  // enforced server-side -- see its comment in
  // 0019_week_plan_visibility_lockdown.sql. This function is only ever
  // wired to a button in PlanPage.jsx, which only ever operates on the
  // signed-in household's own current week, so that's never a live
  // concern here -- it matters once a foreign-household editor exists.
  function clearCurrentWeek() {
    setPlansByWeek((prev) => ({ ...detachWhere(prev, (src) => src.weekStart === weekStart), [weekStart]: buildDefaultWeek(planDefaults) }));
    clearWeekPlanWeek(householdId, weekStart)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[clearCurrentWeek] failed:", e);
        setSaveError(true);
      });
  }

  // Snack/preserves/medicinal branches await the insert (to get the row's
  // real id) before touching local state, rather than updating optimistically
  // like the meal-slot branch does -- a meal cell's identity is deterministic
  // (household/week/day/slot/dishType, known upfront), but an item row's is
  // server-generated, so there's nothing to optimistically render it under
  // until the insert returns.
  async function assignRecipeToPlan(recipeId, mealSlot, dishType, day) {
    if (!recipeId || !mealSlot) return;
    try {
      if (mealSlot === "snack" || mealSlot === "preserves" || mealSlot === "medicinal") {
        const field = mealSlot === "snack" ? "snacks" : mealSlot;
        const itemId = await addWeekPlanItem(householdId, weekStart, mealSlot, recipeId, weekMeta);
        updateCurrentWeek((wd) => ({ ...wd, [field]: [...(wd[field] || []), { id: itemId, recipeId }] }));
        setSaveError(false);
        return;
      }
      const targets = day === "every day" ? DAYS : [day];
      updateCurrentWeek((wd) => {
        const days = { ...wd.days };
        const leftovers = { ...(wd.leftovers || {}) };
        targets.forEach((d) => {
          const daySlots = { ...(days[d] || {}) };
          const mealDishes = { ...normalizeDishes(daySlots[mealSlot]) };
          mealDishes[dishType] = recipeId;
          daySlots[mealSlot] = mealDishes;
          days[d] = daySlots;
          delete leftovers[`${d}|${mealSlot}|${dishType}`];
        });
        return { ...wd, days, leftovers };
      });
      syncLinks(targets.map((d) => ({ day: d, slot: mealSlot, dishType, recipeId, isLeftover: false })));
      await Promise.all(targets.map((d) => saveWeekPlanMeal(householdId, weekStart, d, mealSlot, dishType, recipeId, false, weekMeta)));
      setSaveError(false);
    } catch (e) {
      console.error("[assignRecipeToPlan] failed:", e);
      setSaveError(true);
    }
  }

  async function removeRecipeFromPlan(recipeId, mealSlot, dishType, day) {
    if (!recipeId || !mealSlot) return;
    try {
      if (mealSlot === "snack" || mealSlot === "preserves" || mealSlot === "medicinal") {
        const field = mealSlot === "snack" ? "snacks" : mealSlot;
        const list = weekData[field] || [];
        const match = list.find((entry) => entry.recipeId === recipeId);
        if (!match) return;
        updateCurrentWeek((wd) => {
          const currentList = wd[field] || [];
          const idx = currentList.findIndex((entry) => entry.id === match.id);
          if (idx === -1) return wd;
          const next = [...currentList];
          next.splice(idx, 1);
          return { ...wd, [field]: next };
        });
        await removeWeekPlanItem(match.id);
        setSaveError(false);
        return;
      }
      const targets = day === "every day" ? DAYS : [day];
      const changed = [];
      updateCurrentWeek((wd) => {
        const days = { ...wd.days };
        const leftovers = { ...(wd.leftovers || {}) };
        targets.forEach((d) => {
          if (days[d]?.[mealSlot]?.[dishType] === recipeId) {
            const daySlots = { ...days[d] };
            const mealDishes = { ...normalizeDishes(daySlots[mealSlot]) };
            if (dishType === "entree") {
              mealDishes[dishType] = "";
            } else {
              delete mealDishes[dishType];
            }
            daySlots[mealSlot] = mealDishes;
            days[d] = daySlots;
            delete leftovers[`${d}|${mealSlot}|${dishType}`];
            changed.push(d);
          }
        });
        return { ...wd, days, leftovers };
      });
      syncLinks(changed.map((d) => ({ day: d, slot: mealSlot, dishType, recipeId: "", isLeftover: false })));
      await Promise.all(
        changed.map((d) =>
          dishType === "entree"
            ? saveWeekPlanMeal(householdId, weekStart, d, mealSlot, dishType, "", false, weekMeta)
            : deleteWeekPlanMealRow(householdId, weekStart, d, mealSlot, dishType)
        )
      );
      setSaveError(false);
    } catch (e) {
      console.error("[removeRecipeFromPlan] failed:", e);
      setSaveError(true);
    }
  }

  function recipePlanDates(recipeId) {
    const entries = [];
    Object.keys(plansByWeek)
      .sort()
      .forEach((weekKey) => {
        const wd = plansByWeek[weekKey];
        DAYS.forEach((day) => {
          MEAL_SLOTS.forEach((slot) => {
            const dishes = wd.days?.[day]?.[slot] || {};
            Object.entries(dishes).forEach(([dishType, id]) => {
              if (id === recipeId) entries.push({ weekKey, label: `${day} ${slot} (${dishType})` });
            });
          });
        });
        (wd.snacks || []).forEach((entry) => {
          if (entry.recipeId === recipeId) entries.push({ weekKey, label: "Snack" });
        });
        (wd.preserves || []).forEach((entry) => {
          if (entry.recipeId === recipeId) entries.push({ weekKey, label: "Preserves" });
        });
        (wd.medicinal || []).forEach((entry) => {
          if (entry.recipeId === recipeId) entries.push({ weekKey, label: "Medicinal" });
        });
      });
    return entries;
  }

  const recipeById = useCallback((id) => recipes.find((r) => r.id === id), [recipes]);

  // Mirrors the server-side can_edit_recipe_content() check (see
  // supabase/migrations/0011_recipe_shares.sql) for UI gating -- never the
  // actual authority (RLS + the recipes_guard_immutable_fields trigger are
  // that), just what decides whether Edit/the form's editable fields show
  // up at all. Public recipes are never user-editable; an owned recipe is
  // frozen only while it has an active share with editableByOthers = false;
  // a recipe shared with you is editable only when editableByOthers = true.
  const canEditRecipeContent = useCallback(
    (recipe) => {
      if (!recipe || recipe.isPublic) return false;
      if (recipe.ownerUserId === userId) {
        return !mySharedRecipeIds.has(recipe.id) || recipe.editableByOthers;
      }
      return sharedWithMeIds.has(recipe.id) && recipe.editableByOthers;
    },
    [userId, mySharedRecipeIds, sharedWithMeIds]
  );

  // Which of the two favorite mechanisms applies -- see toggleFavorite and
  // db.js's recipe_favorites comment for why there are two.
  const isFavorite = useCallback(
    (recipe) => (recipe && recipe.ownerUserId === userId ? !!recipe.favorite : myFavoriteRecipeIds.has(recipe?.id)),
    [userId, myFavoriteRecipeIds]
  );

  const allPlannedRecipes = useMemo(() => {
    const list = [];
    DAYS.forEach((day) => {
      MEAL_SLOTS.forEach((slot) => {
        const dishes = normalizeDishes(weekData.days[day]?.[slot]);
        Object.values(dishes).forEach((id) => {
          if (id) list.push(id);
        });
      });
    });
    (weekData.snacks || []).forEach((entry) => list.push(entry.recipeId));
    (weekData.preserves || []).forEach((entry) => list.push(entry.recipeId));
    (weekData.medicinal || []).forEach((entry) => list.push(entry.recipeId));
    return list;
  }, [weekData]);

  // Same as allPlannedRecipes but skips any placement marked as leftovers,
  // since leftovers don't need their own batch of ingredients bought.
  // Each cooked dish at its scale (planScaling.ts); leftovers add nothing.
  // Shared with Home's grocery count.
  const groceryPlannedRecipes = useMemo(() => groceryPlacements(plansByWeek, weekStart, recipeById, weekData), [plansByWeek, weekStart, recipeById, weekData]);

  const weeklyTotalTime = useMemo(() => weekCookMinutes(weekData, recipeById), [weekData, recipeById]);

  const weekServings = weekData.servings || 4;

  const groceryList = useMemo(
    () => buildGroceryList(groceryPlannedRecipes, recipeById),
    [groceryPlannedRecipes, recipeById]
  );

  const totalGroceryItems = Object.values(groceryList).reduce((s, arr) => s + arr.length, 0);

  // Create/edit persist immediately (a single targeted db.js call), rather
  // than relying on the old debounced whole-array sync -- see db.js's
  // "recipes" section header comment for why that model no longer works
  // once the loaded `recipes` array can hold other users' rows. Throws on
  // failure so the caller (RecipesPage) can decide what to do -- e.g. keep
  // the edit form open instead of discarding the user's changes.
  // Public or private, from the share sheet (slice 8a): just that column.
  async function setRecipePublic(recipeId, isPublic) {
    await dbSetRecipePublic(recipeId, isPublic);
    setRecipes((rs) => rs.map((r) => (r.id === recipeId ? { ...r, isPublic: !!isPublic } : r)));
  }

  // Also records "editable by others" as set by the first share, so the
  // recipe in hand agrees with the database.
  function noteEditableByOthers(recipeId, value) {
    setRecipes((rs) => rs.map((r) => (r.id === recipeId ? { ...r, editableByOthers: !!value } : r)));
  }

  // Resolves to the recipe's id (a new recipe's is made here).
  async function saveRecipe(recipe) {
    try {
      let id = recipe.id;
      if (recipe.id) {
        // favorite is owner-only in the UI (see the Known limitations note
        // in the sharing plan -- it's a shared column, not per-user), but a
        // non-owner with content-edit rights still passes the whole form
        // through this same path. Preserve whatever favorite value is
        // already on record rather than trusting a non-owner's (hidden,
        // but not impossible to have gone stale) form state for it.
        const existing = recipes.find((r) => r.id === recipe.id);
        const toSave = existing && existing.ownerUserId !== userId ? { ...recipe, favorite: existing.favorite } : recipe;
        await dbUpdateRecipe(toSave);
        setRecipes((rs) => rs.map((r) => (r.id === recipe.id ? toSave : r)));
      } else {
        // createdAt is the client's clock until the next load brings the
        // row's own created_at; it only orders Home's "New recipes".
        const created = { ...recipe, id: uid(), ownerUserId: userId, createdAt: new Date().toISOString() };
        await dbCreateRecipe(created, userId);
        setRecipes((rs) => [...rs, created]);
        id = created.id;
      }
      setSaveError(false);
      return id;
    } catch (e) {
      console.error("[saveRecipe] failed:", e);
      setSaveError(true);
      throw e;
    }
  }

  async function importRecipes(newRecipes) {
    const withIds = newRecipes.map((r) => ({ ...r, id: uid() }));
    try {
      await dbImportRecipes(withIds, userId);
      const createdAt = new Date().toISOString();
      setRecipes((rs) => [...rs, ...withIds.map((r) => ({ ...r, ownerUserId: userId, isPublic: false, editableByOthers: false, createdAt }))]);
      setSaveError(false);
      return withIds;
    } catch (e) {
      console.error("[importRecipes] failed:", e);
      setSaveError(true);
      return [];
    }
  }

  // Fetches + parses a recipe from a pasted URL (via the import-recipe-
  // from-url edge function) but does not save it -- returns { recipe,
  // dedupRecipeId, normalizedUrl } for the caller to normalize/preview,
  // same as a parsed JSON file, before calling importRecipes (fresh) or
  // claimRecipeUrlImport (dedup hit) to actually persist it.
  async function importRecipeFromUrl(url) {
    return dbImportRecipeFromUrl(url);
  }

  // Registers a freshly-imported recipe as the canonical import for the URL
  // it came from, so a later importer of the same URL gets linked to it
  // instead of creating a duplicate -- best-effort, see
  // registerRecipeUrlImport's own comment in db.js for why a failure here
  // is swallowed rather than surfaced.
  async function registerRecipeUrlImport(normalizedUrl, recipeId) {
    try {
      await dbRegisterRecipeUrlImport(normalizedUrl, recipeId);
    } catch (e) {
      console.error("[registerRecipeUrlImport] failed:", e);
    }
  }

  // Grants the signed-in user access to a recipe someone else already
  // imported from the same URL, then refreshes local state so it shows up
  // immediately, correctly marked as shared/non-editable.
  async function claimRecipeUrlImport(recipeId) {
    await dbClaimRecipeUrlImport(recipeId);
    await refreshRecipesAndShares();
  }

  // Re-runs the two loads that change after a claim: the recipes list
  // itself, and which of the visible recipes are "shared with me" (drives
  // the non-editable UI treatment). Mirrors the calls already made in the
  // initial load effect above -- deliberately narrower than a full reload
  // since nothing else (favorites, week plans) changes from a claim.
  async function refreshRecipesAndShares() {
    try {
      const [freshRecipes, freshSharedWithMeIds] = await Promise.all([loadRecipes(), loadSharedWithMeRecipeIds(userId)]);
      setRecipes(freshRecipes);
      setSharedWithMeIds(freshSharedWithMeIds);
      setSaveError(false);
    } catch (e) {
      console.error("[refreshRecipesAndShares] failed:", e);
      setSaveError(true);
    }
  }

  // Routes to whichever favorite mechanism applies to this recipe for this
  // user -- the owner's own favorite is still the single `recipes.favorite`
  // column (unchanged); anyone else favorites via the per-user
  // recipe_favorites table instead, so their toggle doesn't change what
  // the owner or other viewers of the same public/shared recipe see.
  function toggleFavorite(id) {
    const recipe = recipes.find((r) => r.id === id);
    if (!recipe) return;
    if (recipe.ownerUserId === userId) {
      const next = !recipe.favorite;
      setRecipes((rs) => rs.map((r) => (r.id === id ? { ...r, favorite: next } : r)));
      dbToggleFavorite(id, next)
        .then(() => setSaveError(false))
        .catch((e) => {
          console.error("[toggleFavorite] failed:", e);
          setSaveError(true);
        });
      return;
    }
    const next = !myFavoriteRecipeIds.has(id);
    setMyFavoriteRecipeIds((ids) => {
      const nextIds = new Set(ids);
      if (next) nextIds.add(id);
      else nextIds.delete(id);
      return nextIds;
    });
    dbSetMyRecipeFavorite(id, next)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[toggleFavorite] failed:", e);
        setSaveError(true);
      });
  }

  function scaleRecipe(recipe, factor) {
    const scaled = {
      ...recipe,
      servings: Math.max(1, Math.round(recipe.servings * factor)),
      ingredients: recipe.ingredients.map((ing) =>
        ing.type === "note" ? ing : { ...ing, quantity: formatQuantity(round(parseQuantity(ing.quantity) * factor, 3)) }
      ),
    };
    setRecipes((rs) => rs.map((r) => (r.id === recipe.id ? scaled : r)));
    dbUpdateRecipe(scaled)
      .then(() => setSaveError(false))
      .catch((e) => {
        console.error("[scaleRecipe] failed:", e);
        setSaveError(true);
      });
    return scaled;
  }

  // The plansByWeek cleanup below is purely cosmetic now, not a save
  // trigger -- week_plan_meals.recipe_id is ON DELETE SET NULL and
  // week_plan_items.recipe_id is ON DELETE CASCADE
  // (0012_recipe_visibility_lockdown.sql), so the database already
  // self-corrects the moment dbDeleteRecipe's DELETE succeeds. This just
  // keeps the local UI from showing a stale reference until the next full
  // reload; there's no corresponding db.js call to make here.
  // Named for what it does from the caller's point of view -- "this
  // recipe leaves my library" -- regardless of what that resolves to
  // server-side (plain delete, ownership transfer to another holder, or
  // just removing your own share; see leave_or_delete_recipe in
  // supabase/migrations/0032). The local cleanup below is identical in
  // all three cases: it's gone from view, so strip it and any of your own
  // plan references to it.
  function deleteRecipe(id) {
    setRecipes((rs) => rs.filter((r) => r.id !== id));
    dbLeaveOrDeleteRecipe(id).catch((e) => {
      console.error("[deleteRecipe] failed:", e);
      setSaveError(true);
    });
    setPlansByWeek((prev) => {
      const next = {};
      Object.entries(prev).forEach(([wk, wd]) => {
        const days = {};
        const leftovers = { ...(wd.leftovers || {}) };
        Object.entries(wd.days || {}).forEach(([day, slots]) => {
          const newSlots = {};
          Object.entries(slots || {}).forEach(([mealSlot, dishes]) => {
            const newDishes = { ...dishes };
            Object.keys(newDishes).forEach((dt) => {
              if (newDishes[dt] === id) {
                delete newDishes[dt];
                delete leftovers[`${day}|${mealSlot}|${dt}`];
              }
            });
            newSlots[mealSlot] = newDishes;
          });
          days[day] = newSlots;
        });
        next[wk] = {
          ...wd,
          days,
          snacks: (wd.snacks || []).filter((entry) => entry.recipeId !== id),
          preserves: (wd.preserves || []).filter((entry) => entry.recipeId !== id),
          medicinal: (wd.medicinal || []).filter((entry) => entry.recipeId !== id),
          leftovers,
        };
      });
      return next;
    });
  }

  return {
    loaded,
    householdId,
    userId,
    recipes,
    setRecipes,
    sharedWithMeIds,
    mySharedRecipeIds,
    canEditRecipeContent,
    isFavorite,
    plansByWeek,
    setPlansByWeek,
    planDefaults,
    setPlanDefaults,
    weekStart,
    setWeekStart,
    saveError,
    weekData,
    updateCurrentWeek,
    setDish,
    removeDishRow,
    placeLeftovers,
    moveMeal,
    reloadWeekPlans,
    setCooked,
    setRecipePublic,
    noteEditableByOthers,
    setDayBudget,
    clearDay,
    addWeekItem,
    removeWeekItem,
    applyWeekAsTemplate,
    applyPublicTemplateAsCurrentWeek,
    clearCurrentWeek,
    assignRecipeToPlan,
    removeRecipeFromPlan,
    recipePlanDates,
    recipeById,
    allPlannedRecipes,
    groceryPlannedRecipes,
    weeklyTotalTime,
    weekServings,
    groceryList,
    totalGroceryItems,
    inventoryItems,
    addInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    saveRecipe,
    importRecipes,
    importRecipeFromUrl,
    registerRecipeUrlImport,
    claimRecipeUrlImport,
    toggleFavorite,
    scaleRecipe,
    deleteRecipe,
  };
}
